import { Form } from '@hologram-dimension/form';
import { Panel } from '@hologram-dimension/panel';
import { RadioField } from '@hologram-dimension/radio-field';
import { SelectField } from '@hologram-dimension/select-field';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { CheckboxField } from '@hologram-dimension/checkbox-field';
import ErrorBoundary from 'components/common/ErrorBoundary';
import CarrierForm from 'components/plans/PlanPanel/CarrierForm/index';
import DetailsPanel from 'components/plans/PlanPanel/DetailsPanel/index';
import HelpPanel from 'components/plans/PlanPanel/HelpPanel';
import ZoneForm from 'components/plans/PlanPanel/ZoneForm/index';
import React from 'react';
import * as styles from './PlanPanel.module.scss';

class PlanPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showHelp: false,
    };
  }

  handlePlanChange = (e) => {
    const { onUpdatePlan } = this.props;
    onUpdatePlan(e.target.name, e.target.value);
  };

  handlePlanChangeNumberOnly = (e) => {
    const { onUpdatePlan } = this.props;
    // regex to only allow whole numbers
    const expression = /^[0-9]\d*$/;
    const { name, value } = e.target;

    if (expression.test(value) || value === '') {
      onUpdatePlan(name, value);
    }
  };

  handleRegionChange = (e) => {
    const { onUpdatePlan } = this.props;
    onUpdatePlan('region', e.target.value);
  };

  handleVariableRatesChange = (e) => {
    const { onUpdatePlan } = this.props;
    const isChecked = e?.target?.checked ?? false;
    onUpdatePlan('use_variable_rates', isChecked);
  };

  handleDynamicPoolChange = (e) => {
    const { onUpdatePlan } = this.props;
    const isChecked = e?.target?.checked ?? false;
    onUpdatePlan('is_dynamic_pool', isChecked);
  };

  toggleHelp = (e) => {
    const { value } = e.target;
    this.setState((prevState) => ({
      ...prevState,
      showHelp: value === 'showHelp',
    }));
  };

  render() {
    const {
      orgId,
      orgName,
      planData,
      group,
      onUpdateZone,
      isEditPage,
      editedZone,
      onUpdateCSP,
      regions,
    } = this.props;
    const { showHelp } = this.state;

    const REGION_OPTIONS = regions
      ? [
          {
            value: '0',
            label: 'None',
          },
          ...regions
            .map((region) => ({
              value: region.id.toString(),
              label: region.display_name,
            }))
            .reverse(),
        ]
      : [''];

    return (
      <ErrorBoundary>
        <Panel header={orgId ? `Add Plan to ${orgName} (${orgId})` : 'Plans'}>
          <div className={styles.panel__container}>
            <div className={styles.panelItem}>
              <Form>
                <h2 className={styles.subHeading}>Plan</h2>
                <TextInputField
                  type="text"
                  label="Plan name"
                  placeholder="name"
                  name="name"
                  defaultValue={planData.name}
                  onChange={this.handlePlanChange}
                  required
                />
                <TextInputField
                  type="number"
                  label="Included data (bytes)"
                  placeholder="Data"
                  name="data"
                  defaultValue={planData.data}
                  onChange={this.handlePlanChangeNumberOnly}
                  required
                />
                <CheckboxField
                  name="use_variable_rates"
                  label="Network-level billing"
                  helpText="Marking a plan as NLB will display variable rates in dashboard and the plan will
                  default to billing using NLB rate cards. Sets `use_variable_rates` to true. Flat
                  rates (overage_rate) MUST be set in case this setting is turned off."
                  options={[
                    {
                      label: 'Plan uses variable rates',
                      value: 'use_variable_rates',
                      defaultChecked: planData?.use_variable_rates || false,
                    },
                  ]}
                  onChange={this.handleVariableRatesChange}
                />
                <h2 className={styles.subHeading}>Plan configuration</h2>
                <SelectField
                  label="Data plan type"
                  helpText="Is this a Standard data plan providing Website pricing? Data plans created for a managed account MSA should always be Custom."
                  name="display_category"
                  defaultValue={planData.display_category}
                  onChange={this.handlePlanChange}
                  options={[
                    { value: 'custom', label: 'Custom data plan' },
                    { value: 'standard', label: 'Standard/Default data plan' },
                  ]}
                  required
                />
                <SelectField
                  label="Region"
                  helpText="Groups Standard Website data plans in the Activation and Change Plan workflows."
                  name="region"
                  defaultValue={planData.region}
                  onChange={this.handleRegionChange}
                  options={REGION_OPTIONS}
                  required={planData.display_category === 'standard'}
                />
                <CheckboxField
                  name="is_dynamic_pool"
                  label="Dynamic data pool"
                  helpText="Marks a plan as a dynamic data pool for display in dashboard and reporting
                  purposes."
                  options={[
                    {
                      label: 'Plan is dynamic data pool',
                      value: 'is_dynamic_pool',
                      defaultChecked: planData?.is_dynamic_pool || false,
                    },
                  ]}
                  onChange={this.handleDynamicPoolChange}
                />
              </Form>
            </div>
            <div className={styles.panelItem}>
              <h2 className={styles.subHeading}>Zone</h2>
              {!isEditPage && <p>Set to `global`. We no longer support multi-zone plans.</p>}
              <ZoneForm zone={planData.zones} onUpdateZone={onUpdateZone} isEditPage={isEditPage} />
            </div>
            <div className={styles.panelItem}>
              <h2 className={styles.subHeading}>Carrier</h2>
              <CarrierForm
                carriers={planData.zones.carriers}
                isEditPage={isEditPage}
                updateZone={onUpdateZone}
                editedZone={editedZone}
                onUpdateCSP={onUpdateCSP}
              />
            </div>
            <div className={styles.panelItem}>
              {!isEditPage && (
                <RadioField
                  name="showPlan"
                  defaultValue="showPlan"
                  options={[
                    { label: 'Show plan', value: 'showPlan', defaultChecked: true },
                    { label: 'Show help', value: 'showHelp' },
                  ]}
                  onChange={this.toggleHelp}
                  controlsLayoutDirection="row"
                />
              )}
              {showHelp || isEditPage ? (
                <HelpPanel />
              ) : (
                <DetailsPanel orgName={orgName} orgId={orgId} planData={planData} group={group} />
              )}
            </div>
          </div>
        </Panel>
      </ErrorBoundary>
    );
  }
}

export default PlanPanel;
